import { ReactNode } from 'react';
import { Box, Stack, Text } from '@chakra-ui/react';
import { Curriculum } from '@/lib/types';

interface CurOutcomesSummaryProps {
  curriculum: Pick<Curriculum, 'learning_outcomes' | 'prerequisites'>;
}

// Box label — sentence case on purpose (not the DS uppercase eyebrow role):
// at in-card sizes all-caps reads as shouting, and these are captions over
// reference text, not section headers.
function SummaryLabel({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Text fontSize='xs' fontWeight='600' color={color} mb={1}>
      {children}
    </Text>
  );
}

// Read-only display of the curriculum-wide learning outcomes and prerequisites,
// shared by the browse card (cur-browse-screen) and the editor's plan picker
// (cur-plans-screen) — the two places someone is deciding *which* curriculum to
// use, which is what this text is for. Each box renders only when it has a
// value, so a curriculum with neither adds no empty chrome.
//
// Learning outcomes take the violet tag slot with a matching border and
// prerequisites stay on the neutral surface: outcomes are the promise being
// evaluated, prerequisites the caveat. Violet is a fixed DS tag-slot color, not
// `brand` — brand belongs to the workspace's own controls (buttons, links), so
// tinting reference material with it would put the loudest color in the app on
// text nobody acts on. Same reasoning as cur-structure-screen's slot3 banner.
export default function CurOutcomesSummary({ curriculum }: CurOutcomesSummaryProps) {
  if (!curriculum.learning_outcomes && !curriculum.prerequisites) return null;

  return (
    <Stack gap={2}>
      {curriculum.learning_outcomes && (
        <Box
          bg='slot4.bg'
          borderWidth='1px'
          borderColor='slot4.fg'
          borderRadius='lg'
          px={4}
          py={3}
        >
          <SummaryLabel color='slot4.fg'>Learning outcomes</SummaryLabel>
          <Text fontSize='sm' color='fg' whiteSpace='pre-wrap'>
            {curriculum.learning_outcomes}
          </Text>
        </Box>
      )}

      {curriculum.prerequisites && (
        <Box bg='bg.subtle' borderRadius='lg' px={4} py={3}>
          <SummaryLabel color='fg.muted'>Prerequisites</SummaryLabel>
          <Text fontSize='sm' color='fg' whiteSpace='pre-wrap'>
            {curriculum.prerequisites}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
